import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const heroImage = "/hero/terrea.png";

const LINES = ["We design", "moments —", "not just", "spaces."];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: 0.2 + i * 0.14,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden bg-bone" data-testid="hero-section">
      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute top-20 md:top-24 left-0 right-0 z-20"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-center text-[10px] uppercase tracking-[0.3em] text-muteInk">
          <span className="hidden md:inline-flex items-center gap-2">
            <Link to="/work?category=Hospitality" data-testid="hero-link-hospitality" className="hover:text-ink transition-colors duration-300">Hospitality</Link>
            <span className="opacity-50">·</span>
            <Link to="/work?category=Luxury%20Residential" data-testid="hero-link-residential" className="hover:text-ink transition-colors duration-300">Residential</Link>
            <span className="opacity-50">·</span>
            <Link to="/work?category=Commercial" data-testid="hero-link-commercial" className="hover:text-ink transition-colors duration-300">Commercial</Link>
          </span>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        {/* Left kinetic headline */}
        <motion.div style={{ y: contentY }} className="col-span-12 md:col-span-8">
          <h1 className="font-serif font-light text-ink text-[13vw] leading-[0.9] md:text-[7.2vw] md:leading-[0.94] tracking-[-0.03em]">
            {LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={i === 1 ? "italic text-clay" : ""}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-14 max-w-md text-ink/75 leading-relaxed"
          >
            An Interior Design studio composing Hospitality, Luxury Residential and Commercial spaces —
            from the first line of thought to the final finish, over the last decade.
          </motion.div>
        </motion.div>

        {/* Right clipped image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1.2 }}
          className="col-span-12 md:col-span-4 relative"
        >
          <div className="relative w-full h-[380px] md:h-[520px] overflow-hidden">
            <motion.img
              src={heroImage}
              alt="Editorial interior"
              style={{ y: imageY, scale: imageScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* spotlight */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-bone">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">Featured</p>
                <p className="font-serif text-lg mt-1">Terrea by Wonderla · Bangalore</p>
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">2026</span>
            </div>
          </div>

          {/* Meta beneath */}
          <div className="mt-6 flex items-center justify-between text-[11px] uppercase tracking-[0.28em] text-muteInk">
            <span>Scroll</span>
            <span>↓</span>
          </div>
        </motion.div>

        {/* Bottom row — accolades / counters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-8 mt-10 md:mt-24 pt-10 border-t border-borderWarm"
        >
          {[
            ["10", "Years of practice"],
            ["100+", "Projects delivered"],
            ["03", "Cities across India"],
            ["03", "Disciplines mastered"],
          ].map(([num, label]) => (
            <div key={label} className="flex items-baseline gap-4">
              <span className="font-serif italic text-4xl md:text-5xl text-clay">{num}</span>
              <span className="text-[11px] uppercase tracking-[0.24em] text-muteInk max-w-[140px] leading-snug">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
